import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Grid,
  Spinner,
  Center,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Tag,
  useToast
} from '@chakra-ui/react';
import {
  FiPlus,
  FiSearch,
  FiRefreshCw,
  FiMoreVertical,
  FiTarget,
  FiTrendingUp,
  FiTrendingDown,
  FiUsers,
  FiCheckCircle,
  FiEdit,
  FiEye,
  FiPlayCircle,
  FiPauseCircle,
  FiFileText
} from 'react-icons/fi';
import Pagination from 'react-js-pagination';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import Header from '../../components/Header';
import { getApiHeaders } from '../../utils/DefaultFunction';
import API from '../../helpers/axiosInstance';
import { useNavigate, useLocation } from 'react-router-dom';
import usePagePermissions from '../../hooks/usePagePermissions';
import PageHeading from '../../components/shared/PageHeading';
import { LuFilter } from 'react-icons/lu';

const FunnelList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [funnelsData, setFunnelsData] = useState(null);
  const [funnels, setFunnels] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const { loading: permissionsLoading, can } = usePagePermissions('analytics/funnels');

  const funnelListPermission = can("funnel-list");
  const funnelCreatePermission = can("funnel-create");
  const funnelEditPermission = can("funnel-edit");
  const funnelViewPermission = can("funnel-view");
  const funnelChangeStatusPermission = can("funnel-change-status");
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'green';
      case 'paused':
        return 'yellow';
      case 'draft':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'green.50';
      case 'paused':
        return 'yellow.50';
      case 'draft':
        return 'gray.50';
      default:
        return 'gray.50';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  };
  const onCreateFunnel = (dateString) => {
    navigate('/analytics/funnels-create');
  };
  const onEditFunnel = (funnelId) => {
    navigate(`/analytics/funnels-edit/${funnelId}`);
  };
  const onViewFunnel = (funnelId) => {
    navigate(`/analytics/funnels-view/${funnelId}`);
  };

  // FN04: change a funnel's status via PUT /funnels/:id, then refetch the list.
  const changeFunnelStatus = async (funnelId, status) => {
    try {
      const response = await API.put(
        `${process.env.REACT_APP_API_URL}/funnels/${funnelId}`,
        { status },
        { headers: { ...getApiHeaders(), 'content-type': 'application/json' } }
      );

      if (response.data.status) {
        toast({
          title: `Funnel ${status === 'active' ? 'activated' : status === 'paused' ? 'paused' : 'set as draft'}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
        fetchFunnelData(currentPage);
      }
    } catch (error) {
      console.error('Error updating funnel status:', error);
      toast({
        title: 'Error updating funnel status',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  // `force` (the manual Refresh button) appends refresh=1 so the backend bypasses
  // its 1-hour Redis cache and re-computes + re-caches. Normal loads use the cache.
  const fetchFunnelData = async (page = 1, force = false) => {
    setLoading(true);
    try {
      const response = await API.get(
        `${process.env.REACT_APP_API_URL}/funnels/dashboard?page=${page}&limit=${itemsPerPage}${force ? '&refresh=1' : ''}`,
        { headers: getApiHeaders() }
      );

     

      if (response.data.status) {
        setFunnelsData(response.data.data);
        setFunnels(response.data.data.funnels || []);
      }
    } catch (error) {
      console.error('Error fetching funnel data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (permissionsLoading || !can('funnel-list')) return;
    fetchFunnelData(currentPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsLoading, currentPage]);

  // FN13: after a create (or edit) redirects back here with the funnelSaved
  // signal, force a fresh fetch (refresh=1 also busts the analytics cache) so
  // the new funnel + Total Funnels count show without a manual Refresh. Clear
  // the flag so a later back-navigation doesn't re-trigger it.
  useEffect(() => {
    if (location.state?.funnelSaved) {
      fetchFunnelData(1, true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.funnelSaved]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const filteredFunnels = funnels.filter(funnel => {
    const matchesSearch =
      funnel.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      funnel.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || funnel.status?.toLowerCase() === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calculate stats from API data
  const cards = funnelsData?.cards || {};
  const totalFunnels = cards.total_funnels || 0;
  const activeCount = cards.funnels_by_status?.active || 0;
  const pausedCount = cards.funnels_by_status?.paused || 0;
  const draftCount = cards.funnels_by_status?.draft || 0;
  const avgConversionRate = cards.avg_conversion_rate || 0;
  const totalUsers = cards.total_users || 0;
  const totalConversions = cards.conversions || 0;

  const pagination = funnelsData?.pagination || {};
  const totalItems = pagination.total_count || 0;

  if (permissionsLoading) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Center h="400px">
            <Spinner size="xl" color="blue.500" />
          </Center>
        </BoxWrapper>
      </>
    );
  }

  if (!funnelListPermission) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Center h="400px">
            <Box textAlign="center">
              <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                No Access
              </Text>
              <Text fontSize="sm" color="gray.500">
                You don't have permission to view this sections.
              </Text>
            </Box>
          </Center>
        </BoxWrapper>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Center h="400px">
            <Spinner size="xl" color="blue.500" />
          </Center>
        </BoxWrapper>
      </>
    );
  }

  return (
    <>
      <Header />
      <BoxWrapper>
        {/* Header */}
        <PageHeading
          icon={LuFilter}
          title="Funnels"
          subtitle="Analyze user journeys and conversion funnels"
        >
          {funnelCreatePermission && (
            <Button
              bg={"var(--brand-primary)"}
              color={"white"}
              onClick={onCreateFunnel}
              size="sm"
            >
              Create Funnel
            </Button>
          )}
        </PageHeading>

        {/* Quick Stats */}
        <Grid templateColumns="repeat(4, 1fr)" gap={4} mb={6}>
          <Box className='box-style-card' p={5}>
            <Flex align="center" gap={3} mb={3}>
              <Flex
                align="center"
                justify="center"
                w={10}
                h={10}
                borderRadius="lg"
                bg="cyan.50"
              >
                <Box as={FiTarget} color="cyan.500" fontSize="20px" />
              </Flex>
              <Text fontSize="sm" color="gray.600" fontWeight="medium">Total Funnels</Text>
            </Flex>
            <Text fontSize="3xl" fontWeight="bold" mb={1}>{totalFunnels}</Text>
            <Text fontSize="xs" color="gray.500">
              {activeCount} active · {pausedCount} paused · {draftCount} draft
            </Text>
          </Box>

          <Box className='box-style-card' p={5}>
            <Flex align="center" gap={3} mb={3}>
              <Flex
                align="center"
                justify="center"
                w={10}
                h={10}
                borderRadius="lg"
                bg="green.50"
              >
                <Box as={FiTrendingUp} color="green.500" fontSize="20px" />
              </Flex>
              <Text fontSize="sm" color="gray.600" fontWeight="medium">Avg Conversion</Text>
            </Flex>
            <Text fontSize="3xl" fontWeight="bold" mb={1}>{avgConversionRate.toFixed(1)}%</Text>
            <Text fontSize="xs" color="gray.500">Across all funnels</Text>
          </Box>

          <Box className='box-style-card' p={5}>
            <Flex align="center" gap={3} mb={3}>
              <Flex
                align="center"
                justify="center"
                w={10}
                h={10}
                borderRadius="lg"
                bg="blue.50"
              >
                <Box as={FiUsers} color="blue.500" fontSize="20px" />
              </Flex>
              <Text fontSize="sm" color="gray.600" fontWeight="medium">Total Users</Text>
            </Flex>
            <Text fontSize="3xl" fontWeight="bold" mb={1}>{formatNumber(totalUsers)}</Text>
            <Text fontSize="xs" color="gray.500">
              {funnelsData?.window ? `Last ${funnelsData.window.value} ${funnelsData.window.unit}` : 'All time'}
            </Text>
          </Box>

          <Box className='box-style-card' p={5}>
            <Flex align="center" gap={3} mb={3}>
              <Flex
                align="center"
                justify="center"
                w={10}
                h={10}
                borderRadius="lg"
                bg="purple.50"
              >
                <Box as={FiCheckCircle} color="purple.500" fontSize="20px" />
              </Flex>
              <Text fontSize="sm" color="gray.600" fontWeight="medium">Conversions</Text>
            </Flex>
            <Text fontSize="3xl" fontWeight="bold" mb={1}>{formatNumber(totalConversions)}</Text>
            <Text fontSize="xs" color="gray.500">Total conversions</Text>
          </Box>
        </Grid>

        {/* Filters */}
        <Flex gap={4} mb={6}>
          <InputGroup maxW="md" flex={1}>
            <InputLeftElement pointerEvents="none">
              <FiSearch color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search funnels..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg="white"
            />
          </InputGroup>

          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            w="180px"
            bg="white"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="draft">Draft</option>
          </Select>

          <Button
            leftIcon={<FiRefreshCw />}
            variant="outline"
            onClick={() => fetchFunnelData(currentPage, true)}
            bg="white"
          >
            Refresh
          </Button>
        </Flex>

        {/* Funnels Table */}
        <Box className='box-style-card' borderRadius="lg" overflow="hidden">
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th py={4} >
                    Funnel Name
                  </Th>
                  <Th py={4} >
                    Steps
                  </Th>
                  <Th py={4} >
                    Status
                  </Th>
                  <Th py={4} >
                    Total Users
                  </Th>
                  <Th py={4} >
                    Conversion Rate
                  </Th>
                  <Th py={4} >
                    Conversions
                  </Th>
                  <Th py={4} >
                    Last Updated
                  </Th>
                  <Th py={4} textAlign="right">
                    Actions
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredFunnels.length === 0 ? (
                  <Tr>
                    <Td colSpan={8} textAlign="center" py={12}>
                      <Flex direction="column" align="center" gap={3}>
                        <Box as={FiTarget} fontSize="48px" color="gray.300" />
                        <Text color="gray.500" fontSize="md">
                          {searchQuery || statusFilter !== 'all'
                            ? 'No funnels match your search criteria'
                            : 'No funnels created yet'}
                        </Text>
                        {!searchQuery && statusFilter === 'all' && funnelCreatePermission && (
                          <Button
                            leftIcon={<FiPlus />}
                            colorScheme="blue"
                            size="sm"
                            onClick={onCreateFunnel}
                          >
                            Create Your First Funnel
                          </Button>
                        )}
                      </Flex>
                    </Td>
                  </Tr>
                ) : (
                  filteredFunnels.map((funnel) => (
                    <Tr
                      key={funnel._id}
                      _hover={{ bg: "gray.50" }}
                      transition="all 0.2s"
                    >
                      <Td py={4}>
                        <Box>
                          <Text fontWeight="500" fontSize="md" mb={1}>
                            {funnel.name}
                          </Text>
                          <Text fontSize="sm" color="gray.500" noOfLines={1}>
                            {funnel.description}
                          </Text>
                        </Box>
                      </Td>
                      <Td py={4}>
                        <Tag
                          variant="outline"
                          colorScheme="blue"
                          fontSize="xs"
                          size={"sm"}
                          borderRadius="md"
                        >
                          {funnel.steps} steps
                        </Tag>
                      </Td>
                      <Td py={4}>
                        <Tag
                          colorScheme={getStatusColor(funnel.status)}
                          bg={getStatusBgColor(funnel.status)}
                          fontSize="xs"

                          borderRadius="full"
                          textTransform="capitalize"
                        >
                          {funnel.status}
                        </Tag>
                      </Td>
                      <Td py={4} fontWeight="500" fontSize="md">
                        {funnel.status === 'draft' ? (
                          <Text color="gray.400">-</Text>
                        ) : (
                          formatNumber(funnel.total_users)
                        )}
                      </Td>
                      <Td py={4}>
                        <Flex align="center" gap={2}>
                          <Text fontWeight="500" fontSize="md">
                            {funnel.conversion_rate?.toFixed(2)}%
                          </Text>
                          {funnel.conversion_rate > 30 ? (
                            <Box as={FiTrendingUp} color="green.500" />
                          ) : funnel.conversion_rate > 0 ? (
                            <Box as={FiTrendingDown} color="red.500" />
                          ) : null}
                        </Flex>
                      </Td>
                      <Td py={4} fontWeight="500" fontSize="md">
                        {funnel.status === 'draft' ? (
                          <Text color="gray.400">-</Text>
                        ) : (
                          formatNumber(funnel.conversions)
                        )}
                      </Td>
                      <Td py={4}>
                        <Text color="gray.600" fontSize="sm">
                          {formatDate(funnel.last_updated)}
                        </Text>
                      </Td>
                      <Td py={4}>
                        <Flex justify="flex-end" gap={1}>

                          {(funnelViewPermission || funnelEditPermission || funnelChangeStatusPermission) && (
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<FiMoreVertical />}
                              size="sm"
                              variant="ghost"
                              aria-label="More options"

                            />
                            <MenuList>
                              {funnelViewPermission && (
                              <MenuItem icon={<FiEye />} onClick={() => onViewFunnel(funnel._id)}>
                                View Funnel
                              </MenuItem>
                              )}
                              {funnelEditPermission && (
                              <MenuItem icon={<FiEdit />} onClick={() => onEditFunnel && onEditFunnel(funnel._id)}>
                                Edit Funnel
                              </MenuItem>
                              )}
                              {funnelChangeStatusPermission && (
                              <>
                                <MenuDivider />
                                {funnel.status?.toLowerCase() === 'active' ? (
                                  <MenuItem icon={<FiPauseCircle />} onClick={() => changeFunnelStatus(funnel._id, 'paused')}>
                                    Pause
                                  </MenuItem>
                                ) : (
                                  <MenuItem icon={<FiPlayCircle />} onClick={() => changeFunnelStatus(funnel._id, 'active')}>
                                    Activate
                                  </MenuItem>
                                )}
                                {funnel.status?.toLowerCase() !== 'draft' && (
                                  <MenuItem icon={<FiFileText />} onClick={() => changeFunnelStatus(funnel._id, 'draft')}>
                                    Set as Draft
                                  </MenuItem>
                                )}
                              </>
                              )}
                            </MenuList>
                          </Menu>
                          )}
                        </Flex>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </Box>

        {/* Pagination */}
        {totalItems > itemsPerPage && (
          <Flex justify="center" mt={6}>
            <Box className="pagination-wrapper">
              <Pagination
                activePage={currentPage}
                itemsCountPerPage={itemsPerPage}
                totalItemsCount={totalItems}
                pageRangeDisplayed={5}
                onChange={handlePageChange}
                itemClass="page-item"
                linkClass="page-link"
                activeClass="active"
                activeLinkClass="active-link"
                prevPageText="Previous"
                nextPageText="Next"
                firstPageText="First"
                lastPageText="Last"
              />
            </Box>
          </Flex>
        )}

        {/* Pagination Info */}
        {funnelsData?.pagination && (
          <Flex justify="space-between" align="center" mt={4}>
            <Text fontSize="sm" color="gray.600">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} funnels
            </Text>
            <Text fontSize="sm" color="gray.600">
              Page {pagination.current_page} of {pagination.total_pages}
            </Text>
          </Flex>
        )}
      </BoxWrapper>


    </>
  );
};

export default FunnelList;